import React, { useState } from "react";
import { motion } from "framer-motion";
import { IoLocationOutline, IoCallOutline, IoMailOutline, IoHelpCircleOutline } from "react-icons/io5";
import toast from "react-hot-toast";
import axios from "axios";
import { states } from "../../utils/data/index";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Syne:wght@300;400;500;600&display=swap');

.ct-root *{box-sizing:border-box;margin:0;padding:0}
.ct-root{
  --ink:#1E2A3A;
  --green:#4E8A3C;
  --green-light:#7BBF6A;
  --green-pale:#EBF5E7;
  --aqua:#7BBCC8;
  --aqua-pale:#E8F4F7;
  --gold:#C9A96E;
  --cream:#F7F6F2;
  --muted:#6B7A8D;
  --border:rgba(30,42,58,0.1);
  --white:#FFFFFF;
  font-family:'Syne',sans-serif;
  color:var(--ink);
  background:var(--white);
  width:100%;
  overflow-x:hidden;
}
.ct-serif{font-family:'Playfair Display',Georgia,serif}

/* PAGE HEADER */
.ct-header{
  background:linear-gradient(160deg, #C8DFC2 0%, #B8D8D8 50%, #A8C8D4 100%);
  padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem)) 4.5rem;
  position:relative;overflow:hidden;
}
.ct-header::before{
  content:'';position:absolute;inset:0;
  background:
    radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.4) 0%, transparent 55%),
    radial-gradient(ellipse at 15% 80%, rgba(123,188,200,0.2) 0%, transparent 50%);
  pointer-events:none;
}
.ct-header-inner{position:relative;z-index:1}
.ct-header-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:rgba(30,42,58,0.5);margin-bottom:0.75rem}
.ct-header h1{font-family:'Playfair Display',serif;font-size:52px;color:var(--ink);font-weight:400;line-height:1.15;max-width:640px}
.ct-header h1 strong{font-style:italic;color:var(--green)}
.ct-header-sub{font-family:'Syne',sans-serif;font-size:14px;color:var(--muted);line-height:1.8;max-width:500px;margin-top:1.25rem;font-weight:300}

/* MAIN GRID */
.ct-main{padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));display:grid;grid-template-columns:1.5fr 1fr;gap:3.5rem;align-items:start;background:var(--cream)}

/* FORM CARD */
.ct-form-card{background:white;border:1px solid rgba(78,138,60,0.12);border-radius:10px;overflow:hidden;box-shadow:0 2px 12px rgba(30,42,58,0.05)}
.ct-form-band{height:3px;background:linear-gradient(90deg,var(--green),var(--aqua))}
.ct-form-inner{padding:2.5rem}
.ct-form-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:var(--green);margin-bottom:0.5rem}
.ct-form-title{font-family:'Playfair Display',serif;font-size:26px;color:var(--ink);font-weight:400;margin-bottom:2rem}
.ct-form{display:flex;flex-direction:column;gap:1.25rem}
.ct-field{display:flex;flex-direction:column;gap:0.4rem}
.ct-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
.ct-row-3{display:grid;grid-template-columns:1fr 1fr 0.65fr;gap:0.75rem}
.ct-label{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:var(--muted);font-weight:500}
.ct-input,.ct-select,.ct-textarea{
  width:100%;background:var(--cream);border:1px solid var(--border);
  border-radius:6px;padding:0.75rem 1rem;font-size:13px;
  font-family:'Syne',sans-serif;color:var(--ink);outline:none;
  transition:border-color 0.2s,background 0.2s;-webkit-appearance:none;
}
.ct-input::placeholder,.ct-textarea::placeholder{color:rgba(30,42,58,0.3)}
.ct-input:focus,.ct-select:focus,.ct-textarea:focus{border-color:var(--green);background:white}
.ct-select option{background:white;color:var(--ink)}
.ct-textarea{resize:none;min-height:110px;line-height:1.6}
.ct-divider{height:1px;background:var(--border);margin:0.25rem 0}
.ct-submit{
  width:100%;background:var(--ink);color:white;
  font-family:'Syne',sans-serif;font-size:12px;font-weight:500;
  letter-spacing:0.75px;text-transform:uppercase;padding:14px 24px;
  border-radius:6px;border:none;cursor:pointer;transition:background 0.2s;
}
.ct-submit:hover{background:var(--green)}
.ct-submit:disabled{opacity:0.5;cursor:not-allowed}

/* SIDEBAR */
.ct-sidebar{display:flex;flex-direction:column;gap:1.5rem}
.ct-info-card{background:white;border:1px solid rgba(78,138,60,0.12);border-radius:10px;overflow:hidden;box-shadow:0 2px 12px rgba(30,42,58,0.05)}
.ct-info-header{
  background:linear-gradient(160deg, #3A5C35 0%, #4A6A60 100%);
  padding:1.25rem 1.5rem;
}
.ct-info-header-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2px;text-transform:uppercase;color:var(--green-light);margin-bottom:0.3rem}
.ct-info-header h3{font-family:'Playfair Display',serif;font-size:18px;color:white;font-weight:400}
.ct-info-body{padding:1.5rem;display:flex;flex-direction:column;gap:1.25rem}
.ct-info-item{display:flex;align-items:flex-start;gap:0.875rem}
.ct-info-icon{width:36px;height:36px;border-radius:6px;background:var(--green-pale);display:flex;align-items:center;justify-content:center;flex-shrink:0;color:var(--green);font-size:16px}
.ct-info-label{font-family:'Syne',sans-serif;font-size:11px;font-weight:500;color:var(--ink);letter-spacing:0.3px;margin-bottom:0.2rem}
.ct-info-value{font-family:'Syne',sans-serif;font-size:12px;color:var(--muted);line-height:1.6;font-weight:300}
.ct-info-value a{color:var(--green);text-decoration:none}
.ct-info-value a:hover{text-decoration:underline}
.ct-map{border-radius:10px;overflow:hidden;border:1px solid rgba(78,138,60,0.12);height:220px;box-shadow:0 2px 12px rgba(30,42,58,0.05)}
.ct-map iframe{width:100%;height:100%;border:0;display:block}

/* FAQ SECTION */
.ct-faq{padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));background:white}
.ct-faq-header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:3rem;gap:2rem}
.ct-faq-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:var(--green);margin-bottom:0.6rem}
.ct-faq-header h2{font-family:'Playfair Display',serif;font-size:32px;color:var(--ink);font-weight:400;line-height:1.25}
.ct-faq-header p{font-family:'Syne',sans-serif;font-size:13px;color:var(--muted);line-height:1.75;max-width:300px;font-weight:300}
.ct-faq-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1.5rem}
.ct-faq-item{
  background:white;border:1px solid rgba(78,138,60,0.12);
  border-top:3px solid var(--green);
  border-radius:10px;padding:1.75rem;
  transition:all 0.25s;cursor:default;
  box-shadow:0 2px 8px rgba(30,42,58,0.04);
}
.ct-faq-item:hover{background:var(--green-pale);box-shadow:0 4px 16px rgba(78,138,60,0.1)}
.ct-faq-q{display:flex;align-items:flex-start;gap:0.75rem;margin-bottom:0.6rem}
.ct-faq-icon{color:var(--green);font-size:16px;flex-shrink:0;margin-top:1px}
.ct-faq-q h4{font-family:'Syne',sans-serif;font-size:13px;font-weight:500;color:var(--ink);line-height:1.4}
.ct-faq-a{font-family:'Syne',sans-serif;font-size:12px;color:var(--muted);line-height:1.7;padding-left:1.5rem;font-weight:300}
.ct-faq-footer{text-align:center;margin-top:2.5rem;font-family:'Syne',sans-serif;font-size:13px;color:var(--muted)}
.ct-faq-footer a{color:var(--green);font-weight:500;text-decoration:none}
.ct-faq-footer a:hover{text-decoration:underline}

/* RESPONSIVE */
@media (max-width: 900px) {
  .ct-header{padding:3.5rem 1.5rem}
  .ct-header h1{font-size:34px}
  .ct-main{grid-template-columns:1fr;padding:3.5rem 1.5rem;gap:2rem}
  .ct-faq{padding:3.5rem 1.5rem}
  .ct-faq-header{flex-direction:column;align-items:flex-start;gap:1rem}
  .ct-faq-header h2{font-size:26px}
  .ct-faq-header p{max-width:100%}
  .ct-faq-grid{grid-template-columns:1fr}
}
@media (max-width: 480px) {
  .ct-header{padding:2.5rem 1.25rem}
  .ct-header h1{font-size:28px}
  .ct-main{padding:3rem 1.25rem}
  .ct-row{grid-template-columns:1fr}
  .ct-row-3{grid-template-columns:1fr 1fr}
  .ct-form-inner{padding:1.75rem 1.25rem}
  .ct-faq{padding:3rem 1.25rem}
}
@media (min-width: 1440px) {
  .ct-header h1{font-size:60px}
  .ct-faq-header h2{font-size:36px}
}
`;

const faqs = [
  { q: "How can I contact support?", a: "Use the contact form above for immediate response, or email us directly at portal@promedhealthplus.com." },
  { q: "What are your support hours?", a: "Our team is available Monday–Friday, 9am to 6pm EST. Critical support is available 24/7." },
  { q: "Which states do you service?", a: "We proudly service all providers in the states listed in the form dropdown, with plans to expand further soon." },
  { q: "Do you offer training?", a: "Yes, comprehensive training is included for all new partners, covering clinical protocols and reimbursement processes." },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "", facility: "", city: "", state: "",
    zip: "", phone: "", email: "", question: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => setFormData({ name: "", facility: "", city: "", state: "", zip: "", phone: "", email: "", question: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { name, email, phone, question, facility, city, state, zip } = formData;
    if (!name || !facility || !city || !state || !zip || !phone || !email || !question) {
      toast.error("Please fill in all required fields.");
      setIsSubmitting(false); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      setIsSubmitting(false); return;
    }
    try {
      await axios.post("https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/contact-us/", formData);
      toast.success("Your message has been sent. We'll get back to you soon!");
      resetForm();
    } catch (error) {
      console.error("Failed to send message:", error.response?.data || error.message);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="ct-root">

        {/* PAGE HEADER */}
        <motion.div
          className="ct-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="ct-header-inner">
            <div className="ct-header-eyebrow">Contact Us</div>
            <h1 className="ct-serif">
              We're here to<br /><strong>help you thrive.</strong>
            </h1>
            <p className="ct-header-sub">
              Ready to answer your questions and help you advance your practice with our wound care solutions.
            </p>
          </div>
        </motion.div>

        {/* MAIN GRID */}
        <div className="ct-main">

          {/* FORM */}
          <motion.div
            className="ct-form-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="ct-form-band"></div>
            <div className="ct-form-inner">
              <div className="ct-form-eyebrow">Send a Message</div>
              <div className="ct-form-title ct-serif">Get in touch with our team.</div>

              <form onSubmit={handleSubmit} className="ct-form">
                <div className="ct-row">
                  <div className="ct-field">
                    <label className="ct-label">Provider Name</label>
                    <input className="ct-input" type="text" name="name" value={formData.name}
                      onChange={handleChange} required placeholder="Dr. John Smith" />
                  </div>
                  <div className="ct-field">
                    <label className="ct-label">Facility Name</label>
                    <input className="ct-input" type="text" name="facility" value={formData.facility}
                      onChange={handleChange} required placeholder="Clinic or practice name" />
                  </div>
                </div>

                <div className="ct-row-3">
                  <div className="ct-field">
                    <label className="ct-label">City</label>
                    <input className="ct-input" type="text" name="city" value={formData.city}
                      onChange={handleChange} required placeholder="e.g. Philadelphia" />
                  </div>
                  <div className="ct-field">
                    <label className="ct-label">State</label>
                    <select className="ct-select" name="state" value={formData.state}
                      onChange={handleChange} required>
                      <option value="">Select</option>
                      {states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="ct-field">
                    <label className="ct-label">Zip</label>
                    <input className="ct-input" type="text" name="zip" value={formData.zip}
                      onChange={handleChange} required placeholder="19103" />
                  </div>
                </div>

                <div className="ct-row">
                  <div className="ct-field">
                    <label className="ct-label">Phone Number</label>
                    <input className="ct-input" type="tel" name="phone" value={formData.phone}
                      onChange={handleChange} required placeholder="(888) 338-0490" />
                  </div>
                  <div className="ct-field">
                    <label className="ct-label">Email Address</label>
                    <input className="ct-input" type="email" name="email" value={formData.email}
                      onChange={handleChange} required placeholder="you@example.com" />
                  </div>
                </div>

                <div className="ct-field">
                  <label className="ct-label">Your Question</label>
                  <textarea className="ct-textarea" name="question" value={formData.question}
                    onChange={handleChange} required placeholder="How can we help your practice?" />
                </div>

                <div className="ct-divider"></div>
                <button type="submit" className="ct-submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Submit Inquiry"}
                </button>
              </form>
            </div>
          </motion.div>

          {/* SIDEBAR */}
          <motion.div
            className="ct-sidebar"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="ct-info-card">
              <div className="ct-info-header">
                <div className="ct-info-header-eyebrow">Get In Touch</div>
                <h3 className="ct-serif">Contact Information</h3>
              </div>
              <div className="ct-info-body">
                <div className="ct-info-item">
                  <div className="ct-info-icon"><IoLocationOutline /></div>
                  <div>
                    <div className="ct-info-label">Our Main Office</div>
                    <div className="ct-info-value">
                      30839 Thousand Oaks Blvd<br />Westlake Village, CA 91362
                    </div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon"><IoCallOutline /></div>
                  <div>
                    <div className="ct-info-label">Call Us</div>
                    <div className="ct-info-value">
                      <a href="tel:+18883380490">P — (888) 338-0490</a><br />
                      <a href="tel:+12673519182">F — (267) 351-9182</a>
                    </div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon"><IoMailOutline /></div>
                  <div>
                    <div className="ct-info-label">General Inquiry</div>
                    <div className="ct-info-value">
                      <a href="mailto:portal@promedhealthplus.com">portal@promedhealthplus.com</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ct-map">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3298.8!2d-118.8058!3d34.1436!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80e81f3a5a5a5a5a%3A0x5a5a5a5a5a5a5a5a!2s30839%20Thousand%20Oaks%20Blvd%2C%20Westlake%20Village%2C%20CA%2091362!5e0!3m2!1sen!2sus!4v1628173549219!5m2!1sen!2sus"
                allowFullScreen=""
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>

        {/* FAQ */}
        <section className="ct-faq">
          <div className="ct-faq-header">
            <div>
              <div className="ct-faq-eyebrow">FAQ</div>
              <h2 className="ct-serif">Frequently asked<br />questions.</h2>
            </div>
            <p>Still have questions? Browse our most common inquiries below or reach out directly.</p>
          </div>

          <div className="ct-faq-grid">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                className="ct-faq-item"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="ct-faq-q">
                  <IoHelpCircleOutline className="ct-faq-icon" />
                  <h4>{faq.q}</h4>
                </div>
                <p className="ct-faq-a">{faq.a}</p>
              </motion.div>
            ))}
          </div>

          <p className="ct-faq-footer">
            For immediate answers, visit our{" "}
            <a href="/help-center">Help Center</a>.
          </p>
        </section>

      </div>
    </>
  );
};

export default Contact;